import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import type {CLSMetric} from './metric'
import {LayoutShiftProcessor} from './layout-shift-processor'
import {BaseObserver} from '../base/observer'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {onFCP} from 'web-vitals/attribution'

const supportsCLS = ssrSafeWindow && 'LayoutShift' in ssrSafeWindow

/*
 * The CLSObserver is responsible for listening to Performance events and routing them to the entryProcessor.
 * It also manages resetting CLS and reporting it when navigating or hiding a page.
 *
 * CLS observation is gated on FCP to match CrUX behavior — layout shifts before
 * first contentful paint are not counted.
 */
export class CLSObserver extends BaseObserver<CLSMetric, LayoutShift> {
  get softNavEventToListen() {
    return SOFT_NAV_STATE.START
  }

  initializeProcessor() {
    return new LayoutShiftProcessor()
  }

  override get supported(): boolean {
    return !!supportsCLS
  }

  observe(initialLoad = true) {
    if (initialLoad) {
      this.url = ssrSafeWindow?.location.href

      // Gate on FCP to match CrUX: only count layout shifts after first contentful paint
      let started = false
      onFCP(() => {
        if (started) return
        started = true
        this.startObserving(true)
      })
    } else {
      // Defer URL capture to RENDER time when pushState has updated the URL.
      // CLS observation starts immediately to capture layout shifts during
      // the transition, but the URL is attributed to the destination page.
      ssrSafeDocument?.addEventListener(
        SOFT_NAV_STATE.RENDER,
        () => {
          this.url = ssrSafeWindow?.location.href
        },
        {once: true},
      )

      // Soft navigation — start immediately (FCP already happened)
      this.startObserving(false)
    }
  }

  startObserving(buffered: boolean) {
    this.observer = new PerformanceObserver(list => {
      // Microtask delay to work around a Safari bug where the callback
      // is invoked immediately rather than in a separate task.
      // Matches web-vitals v5's observe() implementation.
      const entries = list.getEntries() as LayoutShift[]
      queueMicrotask(() => {
        this.entryProcessor.processEntries(entries)
      })
    })

    this.observer.observe({type: 'layout-shift', buffered})
  }
}
